/**
 * Free tokens — Blockari's `/faucet` (screens/Faucet.tsx @ 17aabed), in 21 Wild's markup: share on X, paste the
 * link to your post, claim. One page, one panel. The states:
 *
 *   guest        an account is needed (the sign-up sheet)
 *   no game yet  finish a challenge game first
 *   ready        1 Share on X · 2 paste the link · 3 Claim (verifying while the server checks)
 *   cooldown     the countdown to the next claim, and the success moment right after a claim
 *
 * Everything the server decides (eligibility, the link, the time left) arrives in GET /api/faucet; the page never
 * works it out from its own clock except to tick the countdown down. The pure parts (labels, errors, which state)
 * are `faucet-copy.ts`; the share flow is `share/faucet-share.ts`; the state itself is `faucet-state.ts`.
 */

import { faucetPostText } from '@/core/faucet-post';
import { faucetClaim, type FaucetState } from '@/net/api';
import { renderFaucetCards, shareFaucet, uploadFaucetCards, type FaucetCards } from '@/share/faucet-share';
import { mountCountdown, type Countdown } from '@/ui/countdown';
import { ERROR_COPY, STATE_CHANGED_CODES, linkFrom, faucetError, viewOf } from '@/ui/faucet-copy';
import { currentFaucet, faucetIsOff, loadFaucet, setFaucet, subscribeFaucet } from '@/ui/faucet-state';
import { el } from '@/ui/hud';
import { ICON_INFO, ICON_PASTE, ICON_SHARE_GLYPH, TopBar, coin, createScreen, type TopBarCallbacks } from '@/ui/screens';

export interface FaucetCallbacks {
  bar: TopBarCallbacks;
  /** "Create a challenge" / "Take one": the no-game state's ways to a first game, and the success state's "Put them to use". */
  onCreate(): void;
  onTake(): void;
  /** A guest asked to make an account (the sign-up sheet). */
  onSignUp(): void;
  /** A claim paid: the new balance and the amount, so the pill and every screen follow at once. */
  onClaimed(balance: number, amount: number): void;
}

/** The three step glyphs: a card with a share arrow, a link between two cards, the coin. 56 px, like home's. */
function glyph(kind: 'share' | 'paste' | 'claim'): HTMLElement {
  const wrap = el('span', 'w21-faucet-glyph');
  wrap.setAttribute('aria-hidden', 'true');
  const stroke = 'rgb(var(--white-rgb) / 0.35)';
  const dim = 'rgb(var(--text-rgb) / 0.16)';
  const lit = 'var(--accent)';
  const warm = 'var(--accent-warm)';
  const ink = 'var(--c-bg-deep)';
  const card = (x: number, y: number, rot: number, fill: string): string =>
    `<g transform="translate(${x} ${y}) rotate(${rot})"><rect x="-15" y="-21" width="30" height="42" rx="4" fill="${fill}" stroke="${stroke}" stroke-width="1.2"/></g>`;
  let body = '';
  if (kind === 'share')
    body =
      card(40, 54, -10, dim) +
      card(58, 50, 8, lit) +
      `<path d="M58 60V38M50 46l8-8 8 8" fill="none" stroke="${ink}" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/>`;
  else if (kind === 'paste')
    body =
      card(36, 52, -8, dim) +
      card(64, 48, 8, dim) +
      `<path d="M44 52h12" fill="none" stroke="${lit}" stroke-width="4" stroke-linecap="round"/><rect x="28" y="46" width="14" height="12" rx="6" fill="none" stroke="${lit}" stroke-width="3.4"/><rect x="58" y="46" width="14" height="12" rx="6" fill="none" stroke="${lit}" stroke-width="3.4"/>`;
  else body = `<circle cx="50" cy="50" r="30" fill="${warm}" opacity="0.95"/><circle cx="50" cy="50" r="22" fill="rgb(var(--ink-rgb) / 0.7)"/><text x="50" y="58" text-anchor="middle" font-family="var(--font-display)" font-weight="700" font-size="22" fill="${lit}">100</text>`;
  wrap.innerHTML = `<svg viewBox="0 0 100 100" width="56" height="56"><g style="filter: drop-shadow(0 0 5px rgb(var(--accent-rgb) / 0.4))">${body}</g></svg>`;
  return wrap;
}

function note(text: string, tone: 'error' | 'warm' | 'plain' = 'plain', live = false): HTMLElement {
  const n = el('div', tone === 'error' ? 'w21-note w21-error' : tone === 'warm' ? 'w21-note w21-warm' : 'w21-note');
  n.innerHTML = ICON_INFO;
  n.append(el('span', undefined, text));
  if (live) n.setAttribute('role', 'alert');
  return n;
}

function emptyBlock(title: string, body: string, actions: HTMLElement[]): HTMLElement {
  const empty = el('div', 'w21-empty');
  empty.append(el('h3', undefined, title), el('p', undefined, body));
  const row = el('div', 'w21-row');
  row.append(...actions);
  empty.append(row);
  return empty;
}

export class FaucetScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;
  readonly #body: HTMLElement;
  readonly #lede: HTMLElement;
  #username: string | null = null;
  #guest = false;
  #loading = true;
  #loadFailed: string | null = null;
  /** Set right after a claim: the cooldown opens with the celebration. */
  #claimed: number | null = null;
  #countdown: Countdown | null = null;
  #unsubscribe: (() => void) | null = null;
  #active = false;
  // The ready state's own: the cards (made once per link), the preview, what was typed.
  #cards: FaucetCards | null = null;
  #cardsFor = '';
  #cardsFailed = false;
  #preview: string | null = null;
  #postUrl = '';
  #verifying = false;
  #error: string | null = null;
  #shared = false;

  constructor(private readonly callbacks: FaucetCallbacks) {
    const { screen, panel } = createScreen('faucet', true, 'main');
    this.root = screen;
    this.root.setAttribute('aria-label', '21 Wild — free tokens');
    this.topBar = new TopBar(callbacks.bar);
    screen.append(this.topBar.root);
    panel.className = 'w21-page';

    const page = el('div', 'w21-faucet-page');
    const card = el('section', 'w21-panel w21-faucet');
    const head = el('div', 'w21-panel-head');
    head.append(el('h2', undefined, 'Free tokens'), el('span', 'w21-chip w21-amber', 'Weekly'));
    this.#lede = el('p', 'w21-panel-lede');
    this.#body = el('div', 'w21-faucet-body');
    card.append(head, this.#lede, this.#body);
    page.append(card);
    panel.append(page);
  }

  /** Whose faucet it is; a guest sees the sign-up state without asking the server. */
  setAccount(username: string | null, isGuest: boolean): void {
    if (username !== this.#username) {
      this.#dropCards();
      this.#claimed = null;
      this.#postUrl = '';
      this.#error = null;
    }
    this.#username = username;
    this.#guest = isGuest;
  }

  show(): void {
    this.#active = true;
    this.#claimed = null;
    this.#error = null;
    this.#verifying = false;
    this.#loadFailed = null;
    this.#unsubscribe?.();
    this.#unsubscribe = subscribeFaucet(() => {
      // Another surface read the state again: repaint only when what it says changed (never under a typing hand).
      const state = this.#state();
      if (!this.#active || this.#verifying || !state) return;
      if (viewOf(state) !== this.#body.dataset.state) this.#render();
    });
    if (this.#guest || !this.#username) {
      this.#loading = false;
      this.#render();
      // Even a guest asks once: the answer may be "off", and then the sign-up state is not what to show.
      if (this.#username) void loadFaucet(this.#username, { force: true }).then(() => this.#active && this.#render());
      return;
    }
    this.#loading = currentFaucet(this.#username) === null;
    this.#render();
    void this.#load();
  }

  hide(): void {
    this.#active = false;
    this.#stopClock();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  async #load(): Promise<void> {
    const name = this.#username;
    if (!name) return;
    const state = await loadFaucet(name, { force: true });
    if (!this.#active || name !== this.#username) return;
    this.#loading = false;
    this.#loadFailed = state ? null : "We couldn't load your free tokens. Check your connection and try again.";
    this.#render();
  }

  #state(): FaucetState | null {
    return this.#username ? currentFaucet(this.#username) : null;
  }

  /** Repaint in place, keeping the caret where the player left it (the cards landing must not cost them their place). */
  #repaint(): void {
    const active = document.activeElement;
    const typing = active instanceof HTMLInputElement && this.#body.contains(active);
    const at = typing ? active.selectionStart : null;
    this.#render();
    if (!typing) return;
    const input = this.#body.querySelector<HTMLInputElement>('.w21-faucet-field input');
    if (!input) return;
    input.focus({ preventScroll: true });
    if (at !== null) input.setSelectionRange(at, at);
  }

  #render(): void {
    this.#stopClock();
    const state = this.#state();
    this.#lede.textContent = `Share 21 Wild on X and get ${state?.amount ?? 100} $CHAIN free, once every 7 days. Paste the link to your post and we do the rest.`;
    this.#body.dataset.state = 'loading';
    // The server says the faucet is off (its flag, or an origin that is not public https): nothing to claim.
    if (faucetIsOff()) {
      this.#body.dataset.state = 'off';
      this.#body.replaceChildren(note(ERROR_COPY['faucet-off'] ?? 'Free tokens are not available right now.'));
      return;
    }
    if (this.#guest) {
      this.#body.dataset.state = 'guest';
      const create = el('button', 'w21-btn w21-primary', 'Create an account') as HTMLButtonElement;
      create.type = 'button';
      create.addEventListener('click', () => this.callbacks.onSignUp());
      this.#body.replaceChildren(
        emptyBlock('Create an account to claim', `The faucet is for registered accounts. Sign up, finish a challenge, and claim ${state?.amount ?? 100} $CHAIN every week.`, [create]),
      );
      return;
    }
    if (!state) {
      if (this.#loadFailed && !this.#loading) {
        this.#body.dataset.state = 'error';
        const retry = el('button', 'w21-btn w21-block', 'Try again') as HTMLButtonElement;
        retry.type = 'button';
        retry.addEventListener('click', () => {
          this.#loading = true;
          this.#loadFailed = null;
          this.#render();
          void this.#load();
        });
        const stack = el('div', 'w21-stack');
        stack.append(note(this.#loadFailed, 'error', true), retry);
        this.#body.replaceChildren(stack);
        return;
      }
      const sk = el('div', 'w21-stack');
      sk.setAttribute('aria-busy', 'true');
      for (const cls of ['w21-skeleton w21-line', 'w21-skeleton w21-line w21-short', 'w21-skeleton w21-line']) sk.append(el('div', cls));
      this.#body.replaceChildren(sk);
      return;
    }
    const view = viewOf(state);
    this.#body.dataset.state = view;
    // The server says this account is a guest (the session moved under the page): show the sign-up state.
    if (view === 'guest') {
      this.#guest = true;
      this.#render();
      return;
    }
    if (view === 'cooldown') this.#body.replaceChildren(this.#cooldown(state));
    else if (view === 'no-game') {
      const create = el('button', 'w21-btn w21-warm', 'Create a challenge') as HTMLButtonElement;
      create.type = 'button';
      create.addEventListener('click', () => this.callbacks.onCreate());
      const take = el('button', 'w21-btn', 'Take one') as HTMLButtonElement;
      take.type = 'button';
      take.addEventListener('click', () => this.callbacks.onTake());
      this.#body.replaceChildren(emptyBlock('Play a challenge first', 'Finish one challenge game and the faucet opens. Practice runs do not count.', [create, take]));
    } else this.#body.replaceChildren(this.#ready(state));
  }

  /* ---------------------------------------------------------------- cooldown */

  #cooldown(state: FaucetState): HTMLElement {
    const nextAt = Date.parse(state.nextClaimAt ?? '');
    const revoked = state.lastClaim?.status === 'revoked';
    const stack = el('div', 'w21-stack w21-faucet-wait');
    stack.setAttribute('aria-live', 'polite');
    if (this.#claimed !== null) {
      const win = el('div', 'w21-faucet-hero w21-up w21-faucet-win');
      win.setAttribute('role', 'status');
      const figure = el('b', 'w21-faucet-figure');
      figure.append(coin(), document.createTextNode(`+${this.#claimed.toLocaleString('en-US')}`), el('small', undefined, '$CHAIN'));
      win.append(el('span', 'w21-t-label', 'Claimed'), figure);
      stack.append(win);
    }
    const clock = el('div', 'w21-faucet-hero w21-faucet-clock');
    // The big clock is ticking text, not news: a screen reader is not told every second.
    const count = el('b', 'w21-faucet-figure');
    count.dataset.testid = 'faucet-countdown';
    count.setAttribute('aria-live', 'off');
    this.#countdown = mountCountdown({ to: nextAt, onElapsed: () => void this.#elapsed() });
    count.append(this.#countdown.root);
    clock.append(el('span', 'w21-t-label', 'Next claim in'), count);
    stack.append(clock);
    if (revoked) {
      stack.append(note('The post from your last claim was removed, so this wait is 14 days. The tokens are yours to keep.', 'warm'));
    } else if (this.#claimed === null && state.lastClaim) {
      stack.append(el('p', 'w21-faucet-fine', `Claimed with @${state.lastClaim.handle}. Keep the post up: it is checked again after a day.`));
    }
    if (this.#claimed !== null) {
      stack.append(el('p', 'w21-faucet-fine', 'Keep your post up: it is checked again after a day, and removing it makes the next wait 14 days.'));
    }
    const use = el('button', 'w21-btn w21-block', 'Put them to use') as HTMLButtonElement;
    use.type = 'button';
    use.addEventListener('click', () => this.callbacks.onTake());
    stack.append(use);

    return stack;
  }

  /**
   * The wait ran out (and again every 5 s while it still shows): read the faucet again. Ready: the page opens up
   * without a reload. The server still reporting a wait: the same clock counts to the time it now gives, and is not
   * rebuilt, so the retries stay five seconds apart.
   */
  async #elapsed(): Promise<void> {
    const name = this.#username;
    if (!name || !this.#active) return;
    const state = await loadFaucet(name, { force: true });
    if (!this.#active || name !== this.#username || !this.#countdown) return;
    // No answer (the call failed): keep counting; the next retry is five seconds on.
    if (!state) return;
    if (!state.guest && state.nextClaimAt !== null) {
      this.#countdown.setTarget(Date.parse(state.nextClaimAt));
      return;
    }
    this.#claimed = null;
    this.#loading = false;
    this.#loadFailed = null;
    this.#render();
  }

  #stopClock(): void {
    this.#countdown?.destroy();
    this.#countdown = null;
  }

  /* ------------------------------------------------------------------- ready */

  #dropCards(): void {
    if (this.#preview) URL.revokeObjectURL(this.#preview);
    this.#preview = null;
    this.#cards = null;
    this.#cardsFor = '';
    this.#cardsFailed = false;
  }

  /** The cards are made (and stored for the link page) as soon as the page is ready, once per link. */
  #prepare(link: string, amount: number): void {
    const key = `${link}|${amount}`;
    if (this.#cardsFor === key) return;
    this.#dropCards();
    this.#cardsFor = key;
    void renderFaucetCards(link, amount).then(
      async (cards) => {
        // Stored before the player can share, so the link unfurls into their own card.
        await uploadFaucetCards(cards);
        if (this.#cardsFor !== key) return;
        this.#cards = cards;
        this.#preview = URL.createObjectURL(cards.story);
        const state = this.#state();
        if (this.#active && state && viewOf(state) === 'ready' && !this.#verifying) this.#repaint();
      },
      () => {
        if (this.#cardsFor !== key) return;
        this.#cardsFailed = true;
        if (this.#active && !this.#verifying) this.#repaint();
      },
    );
  }

  #ready(state: FaucetState): HTMLElement {
    const link = state.link ?? '';
    this.#prepare(link, state.amount);
    const form = el('form', 'w21-stack w21-faucet-form') as HTMLFormElement;
    form.noValidate = true;
    const steps = el('div', 'w21-steps w21-faucet-steps');

    /* 01 — share */
    const s1 = el('div', 'w21-step');
    const b1 = el('div', 'w21-stack w21-tight');
    b1.append(el('span', 'w21-step-n', '01'), el('b', undefined, 'Share on X'), el('span', undefined, `Post your ${state.amount} free tokens card. The text and your personal link are filled in; keep both.`));
    if (this.#preview) {
      const preview = el('div', 'w21-share-preview w21-faucet-preview');
      const img = el('img') as HTMLImageElement;
      img.src = this.#preview;
      img.alt = 'Your free tokens card';
      preview.append(img);
      b1.append(preview);
    }
    const share = el('button', 'w21-btn w21-primary w21-block') as HTMLButtonElement;
    share.type = 'button';
    share.disabled = !this.#cards;
    if (!this.#cards && !this.#cardsFailed) {
      share.append(el('span', 'w21-spinner w21-inline w21-dark'), document.createTextNode(' Preparing'));
    } else {
      share.insertAdjacentHTML('beforeend', ICON_SHARE_GLYPH);
      share.append(document.createTextNode(' Share on X'));
    }
    share.addEventListener('click', () => {
      if (!this.#cards) return;
      share.disabled = true;
      void shareFaucet(this.#cards, link, state.amount)
        .then(() => {
          this.#shared = true;
          input.focus({ preventScroll: true });
          step2.textContent = 'Posted? Open your post on X, copy its link and paste it here.';
        })
        .catch(() => undefined)
        .finally(() => {
          share.disabled = false;
        });
    });
    b1.append(share);
    if (this.#cardsFailed) {
      b1.append(note('The card could not be drawn. Copy the post and share it yourself.', 'error'));
      const copy = el('button', 'w21-btn w21-block', 'Copy post') as HTMLButtonElement;
      copy.type = 'button';
      copy.addEventListener('click', () => {
        void navigator.clipboard?.writeText(faucetPostText(state.amount, link)).then(
          () => (copy.textContent = 'Copied'),
          () => (copy.textContent = "Couldn't copy"),
        );
      });
      b1.append(copy);
    }
    const mine = el('div', 'w21-share-link w21-faucet-link');
    mine.append(el('span', 'w21-t-label', 'Your link'), el('code', undefined, link));
    b1.append(mine);
    s1.append(glyph('share'), b1);

    /* 02 — paste */
    const s2 = el('div', 'w21-step');
    const b2 = el('div', 'w21-stack w21-tight');
    const step2 = el('span', undefined, `${this.#shared ? 'Posted? ' : ''}Open your post on X, copy its link and paste it here.`);
    b2.append(el('span', 'w21-step-n', '02'), el('b', undefined, 'Paste the link to your post'), step2);
    const field = el('div', 'w21-code-field w21-faucet-field');
    // One line, as Blockari's (Faucet.tsx @ 17aabed): a long link scrolls inside the field and never widens the page.
    const input = el('input', 'w21-su-input') as HTMLInputElement;
    input.type = 'text';
    input.inputMode = 'url';
    input.enterKeyHint = 'go';
    input.autocapitalize = 'none';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('autocorrect', 'off');
    input.placeholder = 'https://x.com/you/status/…';
    input.value = this.#postUrl;
    input.disabled = this.#verifying;
    input.setAttribute('aria-label', 'Link to your post on X');
    field.append(input);
    if (typeof navigator.clipboard?.readText === 'function' && window.isSecureContext) {
      const paste = el('button', 'w21-btn w21-paste') as HTMLButtonElement;
      paste.type = 'button';
      paste.setAttribute('aria-label', 'Paste link');
      paste.title = 'Paste';
      paste.innerHTML = ICON_PASTE;
      paste.append(el('span', 'w21-lbl', 'Paste'));
      paste.disabled = this.#verifying;
      paste.addEventListener('click', () => {
        void navigator.clipboard
          .readText()
          .then((t) => {
            input.value = linkFrom(t);
            onInput();
          })
          .catch(() => input.focus());
      });
      field.append(paste);
    }
    b2.append(field);
    s2.append(glyph('paste'), b2);

    /* 03 — claim */
    const s3 = el('div', 'w21-step');
    const b3 = el('div', 'w21-stack w21-tight');
    b3.append(el('span', 'w21-step-n', '03'), el('b', undefined, 'Claim'), el('span', undefined, `We check your post on X and add ${state.amount} $CHAIN to your balance.`));
    const submit = el('button', 'w21-btn w21-primary w21-block w21-lg') as HTMLButtonElement;
    submit.type = 'submit';
    submit.disabled = this.#verifying || this.#postUrl.trim() === '';
    if (this.#verifying) {
      submit.classList.add('w21-busy');
      submit.append(el('span', 'w21-spinner w21-inline w21-dark'), document.createTextNode(' Checking your post'));
    } else {
      submit.append(coin(), document.createTextNode(` Claim ${state.amount} $CHAIN`));
    }
    b3.append(submit);
    s3.append(glyph('claim'), b3);

    steps.append(s1, s2, s3);
    form.append(steps);
    const errorSlot = el('div', 'w21-faucet-error');
    errorSlot.setAttribute('aria-live', 'assertive');
    if (this.#error) errorSlot.append(note(this.#error, 'error'));
    // The button already says "Checking your post"; this is the same words for a screen reader, once, and not on screen.
    if (this.#verifying) {
      const live = el('span', 'w21-faucet-checking w21-sr-live', 'Checking your post on X.');
      live.setAttribute('role', 'status');
      errorSlot.append(live);
    }
    form.append(errorSlot);

    const onInput = (): void => {
      this.#postUrl = input.value;
      if (this.#error) {
        this.#error = null;
        errorSlot.replaceChildren();
        input.removeAttribute('aria-invalid');
      }
      submit.disabled = this.#verifying || input.value.trim() === '';
    };
    input.addEventListener('input', onInput);
    // Enter claims (the form's own implicit submission), except while an IME is composing: that Enter picks a
    // candidate and must not send the form.
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && (event.isComposing || event.keyCode === 229)) event.preventDefault();
    });
    if (this.#error) input.setAttribute('aria-invalid', 'true');
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.#claim(state);
    });
    return form;
  }

  async #claim(state: FaucetState): Promise<void> {
    const name = this.#username;
    if (!name || this.#verifying || this.#postUrl.trim() === '') return;
    this.#verifying = true;
    this.#error = null;
    this.#render();
    const result = await faucetClaim(linkFrom(this.#postUrl));
    this.#verifying = false;
    if (!this.#active || name !== this.#username) return;
    if (result.ok) {
      this.#claimed = result.value.claim.amount;
      this.#postUrl = '';
      this.#dropCards();
      setFaucet(name, result.value.faucet);
      this.callbacks.onClaimed(result.value.claim.balance, result.value.claim.amount);
      this.#render();
      return;
    }
    this.#error = faucetError(result);
    // The cooldown or the eligibility moved under us (or a claim whose answer was lost may have paid): the page
    // reads again and shows the true state.
    const unsure = result.status === undefined || result.status >= 500;
    if (unsure || (result.error !== undefined && STATE_CHANGED_CODES.includes(result.error))) {
      const next = await loadFaucet(name, { force: true });
      if (next && viewOf(next) !== 'ready') this.#error = null;
    }
    if (this.#active) this.#render();
    void state;
  }
}
